import api from './api';

export interface FnoAssessment {
  id: number;
  alertId: number;
  symbol: string;
  direction: string;
  combinedScore: number;
  optionsScore?: number;
  futuresScore?: number;
  regime: 'STRONG' | 'REVIEW' | 'REJECT';
  oiPattern?: string;
  summary?: string;
  optionSignalId?: number;
  futuresSignalId?: number;
  breakdown?: Record<string, any>;
  createdAt: string;
}

export interface FuturesUnderlying {
  id: number;
  symbol: string;
  displayName?: string;
  exchange: string;
  isIndex: boolean;
  lotSize: number;
  tickSize: number;
  isTracked: boolean;
  feedSource: string;
  latestMetrics?: FuturesDerivedMetrics;
  dataStale: boolean;
}

export interface FuturesSnapshot {
  id: number;
  underlyingId: number;
  spotPrice?: number;
  futuresPrice?: number;
  openInterest: number;
  oiChange: number;
  volume: number;
  basis?: number;
  costOfCarry?: number;
  fundingRate?: number;
  feedSource: string;
  capturedAt: string;
}

export interface FuturesDerivedMetrics {
  id: number;
  underlyingId: number;
  oiPattern?: string;
  oiPatternScore?: number;
  volumeSurgeRatio?: number;
  volumeSurgeScore?: number;
  basisOrFunding?: number;
  basisScore?: number;
  rolloverPct?: number;
  rolloverScore?: number;
  priceOiCorrelation?: number;
  correlationScore?: number;
  deliveryPct?: number;
  deliveryScore?: number;
  freshnessScore?: number;
  futuresScore: number;
  scoreBreakdown?: Record<string, any>;
  spotPrice?: number;
  futuresPrice?: number;
  openInterest?: number;
  basis?: number;
  computedAt: string;
}

const fnoService = {
  getUnderlyings: async (): Promise<FuturesUnderlying[]> => {
    const res = await api.get('/fno/underlyings');
    return (res.data as any).data;
  },

  getMetrics: async (symbol: string): Promise<FuturesDerivedMetrics> => {
    const res = await api.get(`/fno/metrics/${symbol}`);
    return (res.data as any).data;
  },

  getSnapshots: async (symbol: string, limit = 40): Promise<FuturesSnapshot[]> => {
    const res = await api.get(`/fno/snapshots/${symbol}?limit=${limit}`);
    return (res.data as any).data;
  },

  getAssessment: async (alertId: number): Promise<FnoAssessment> => {
    const res = await api.get(`/fno/assessment/${alertId}`);
    return (res.data as any).data;
  },

  getAssessmentsBatch: async (alertIds: number[]): Promise<Record<number, FnoAssessment>> => {
    if (!alertIds.length) return {};
    const res = await api.post('/fno/assessments/batch', { alertIds });
    return (res.data as any).data;
  },

  getAssessmentsBySymbol: async (symbol: string): Promise<FnoAssessment[]> => {
    const res = await api.get(`/fno/assessments/${symbol}`);
    return (res.data as any).data;
  },

  assessAlert: async (alertId: number, symbol: string, direction: 'LONG' | 'SHORT'): Promise<FnoAssessment> => {
    // Suppress global toast — "Symbol not tracked" 404 is handled silently in FnoScoreBadge
    const res = await api.post('/fno/assess-alert', { alertId, symbol, direction }, {
      headers: { _suppressToast: 'true' }
    });
    return (res.data as any).data;
  },

  analyzeSymbol: async (symbol: string): Promise<FuturesDerivedMetrics> => {
    const res = await api.post(`/fno/analyze/${symbol}`);
    return (res.data as any).data;
  },

  updateUnderlying: async (id: number, data: Partial<FuturesUnderlying>): Promise<FuturesUnderlying> => {
    const res = await api.put(`/fno/underlyings/${id}`, data);
    return (res.data as any).data;
  }
};

export default fnoService;
